import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import { ShoppingBag, MessagesSquare, Users2, PenLine, Clapperboard, BadgeCheck, Share2 } from "lucide-react";
import { toast } from "sonner";
import { ContentStudio } from "@/components/ContentStudio";
import { OrdersTable, ContactsList, LeadsTable, SharesPanel } from "@/components/admin/AdminTables";
import { ArticlesList, ExpertsList } from "@/components/admin/AdminModeration";

const TABS = [
  { id: "orders", label: "Commandes", icon: ShoppingBag },
  { id: "contacts", label: "Devis & messages", icon: MessagesSquare },
  { id: "articles", label: "Articles proposés", icon: PenLine },
  { id: "experts", label: "Experts", icon: BadgeCheck },
  { id: "leads", label: "Leads", icon: Users2 },
  { id: "shares", label: "Partages", icon: Share2 },
  { id: "studio", label: "Studio Contenu", icon: Clapperboard },
];

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [experts, setExperts] = useState([]);
  const [shares, setShares] = useState(null);

  const loadSubmissions = useCallback(
    () => http.get("/admin/blog-submissions").then((r) => setSubmissions(r.data)).catch(() => {}),
    []
  );

  useEffect(() => {
    if (user?.role !== "admin") return;
    http.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    http.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    http.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    http.get("/admin/expert-applications").then((r) => setExperts(r.data)).catch(() => {});
    http.get("/admin/guide-shares").then((r) => setShares(r.data)).catch(() => {});
    loadSubmissions();
  }, [user, loadSubmissions]);

  const counts = {
    orders: orders.length,
    contacts: contacts.length,
    articles: submissions.filter((s) => s.status === "pending").length,
    experts: experts.length,
    leads: leads.length,
    shares: shares?.total_shares || 0,
  };

  const decide = async (id, action) => {
    try {
      await http.post(`/admin/blog-submissions/${id}/${action}`);
      toast.success(action === "approve" ? "Article publié sur le blog" : "Article refusé");
      loadSubmissions();
    } catch {
      toast.error("Erreur lors de l'action");
    }
  };

  if (user && user.role !== "admin") {
    return <div className="container-x py-24 text-center text-slate-600" data-testid="admin-denied">Accès réservé à l'administration.</div>;
  }

  return (
    <div className="container-x py-12" data-testid="admin-page">
      <div className="eyebrow mb-3">Espace administration</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Gérer Beehive Academy.</h1>

      <div className="mt-8 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === t.id ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
          >
            <t.icon size={15} strokeWidth={1.75} /> {t.label}
            {t.id !== "studio" && (
              <span className={`text-xs rounded-full px-2 py-0.5 ${tab === t.id ? "bg-white/20" : "bg-white"}`}>
                {counts[t.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "orders" && <OrdersTable orders={orders} />}
        {tab === "contacts" && <ContactsList contacts={contacts} />}
        {tab === "articles" && <ArticlesList submissions={submissions} onDecide={decide} />}
        {tab === "experts" && <ExpertsList experts={experts} />}
        {tab === "leads" && <LeadsTable leads={leads} />}
        {tab === "shares" && <SharesPanel data={shares} />}
        {tab === "studio" && <ContentStudio />}
      </div>
    </div>
  );
}
